import { HttpStatusCode, isAxiosError } from 'axios';
import { type ApiPath as SafeURL } from '@/services/apiPath';
import { API_ROUTES } from '@/api/apiRoutes';
import { FIGMA_CONNECTION_QUERY_KEY, type FigmaConnection } from '@/hooks/useLinkedFigmaAccount';
import { log } from '@/logger/logger';
import { queryClient } from '@/providers/queryClient';
import { withRetry } from '@/utils/async.utils';
import { safeAxios } from '@/services/safeAxios';

const logger = log.getLogger('workflowCommunityPublishing.api');

/** DEV-only: route the presigned upload through the Vite dev proxy to dodge the localhost→S3 CORS
 *  block (see vite.config.ts); prod uses the presigned URL as-is. */
const resolveCommunityUploadUrl = (presignedUrl: string): string =>
  import.meta.env.DEV ? presignedUrl.replace(/^https:\/\/[^/]+/, '/__community_s3_upload__') : presignedUrl;

// Weave Core proxies the community and figma-publishing routes to Sinatra with the Figma OAuth token
// it holds server-side. Bodies stay Sinatra's shape, relayed verbatim, so the parsing below is unchanged.

/** Core's 403 reason for "there is no usable Figma grant". */
const NOT_CONNECTED_REASON = 'not_connected_to_figma';

const SERVER_ERROR_STATUS = 500;

export class NotConnectedToFigmaError extends Error {
  constructor() {
    super('No Figma account is linked. Please connect to Figma first.');
    this.name = 'NotConnectedToFigmaError';
  }
}

function isNotConnectedToFigma(error: unknown): boolean {
  return (
    isAxiosError<{ ok?: boolean; reason?: string }>(error) && error.response?.data?.reason === NOT_CONNECTED_REASON
  );
}

/** Sinatra answers 404 when the resource has no listing. */
function isNotPublished(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound;
}

/**
 * Both metadata reads run on every workflow and tool open, so an expected failure reported as a
 * defect buries the real ones: a missing grant and a 4xx are verdicts on the caller, not faults.
 */
function logMetadataFailure(event: string, error: unknown): void {
  if (error instanceof NotConnectedToFigmaError) return;
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status !== undefined && status < SERVER_ERROR_STATUS) {
    logger.warn(event, { status });
    return;
  }
  logger.error(event, { status });
}

/**
 * Runs one proxied request. A missing Figma grant is the only failure interpreted here; everything else,
 * including Sinatra's own 4xx envelopes, is rethrown so existing callers keep their handling.
 */
async function proxied<T>(send: () => Promise<T>): Promise<T> {
  try {
    return await send();
  } catch (error) {
    if (!isNotConnectedToFigma(error)) throw error;
    // Written, not invalidated: a refetch can briefly return the still-stored grant as linked and send
    // callers back into the same failing request.
    queryClient.setQueriesData<FigmaConnection>({ queryKey: [FIGMA_CONNECTION_QUERY_KEY] }, { connected: false });
    throw new NotConnectedToFigmaError();
  }
}

interface SinatraResponseEnvelope<T> {
  error: boolean;
  status: number;
  meta: T;
  i18n: unknown;
}

interface PresignedPost {
  sha1: string;
  url: string;
  fields: Record<string, string>;
}

export interface PublisherProfile {
  /**
   * `int64` upstream, so proto3 sends it as a string. Forwarded verbatim — Figma ids run past
   * `Number.MAX_SAFE_INTEGER`, and `Number()`-ing one truncates it into a different profile.
   */
  id: string;
  name: string;
  profile_handle: string;
  entity_type: string;
  primary_user_id: number;
  img_url: string;
  public_at: string;
}

export interface PublisherProfilesResponse {
  profiles: PublisherProfile[];
  is_banned_from_community_publishing: boolean;
  is_paid_figma_user: boolean;
  /** True once the linked Figma user has accepted the Creator Agreement on any Community surface. */
  has_accepted_community_creator_agreement: boolean;
  /** Community's rollout decision for the linked Figma user; false means the per-publish checkbox. */
  community_user_level_creator_agreement_enabled: boolean;
}

export interface CommunityCategoryTag {
  id: string;
  text: string;
}

export interface CommunityCategory {
  id: string;
  title: string;
  url_slug?: string;
  parent_category_id: string | null;
  tags: CommunityCategoryTag[];
  /** The Community resource types this category accepts; publishing under one that omits it fails. */
  allowed_resource_types?: string[];
}

/** The `resource_type` a Weave publish creates in Community. A tool is a `weave_app`. */
export type CommunityResourceType = 'weave_app' | 'weave_workflow';

export interface CarouselMediaEntry {
  sha1: string;
  carousel_position: number;
}

export interface PublishWorkflowRequest {
  /** Sent as a path segment; kept here so callers still build one object. */
  weave_workflow_id: string;
  name: string;
  description?: string;
  carousel_media: CarouselMediaEntry[];
  weavy_version_number: number;
  /** See {@link PublisherProfile.id}. */
  publisher_profile_id: string;
  category_id?: string;
  tags?: string[];
  support_contact?: string;
  agreed_to_tos: boolean;
}

export interface PublishWorkflowResponse {
  resource_id: string;
  weave_file_id: string;
  version_id: string;
  weavy_version_number: number;
}

export interface WorkflowCommunityVersionMetadata {
  id: string;
  weavy_version_number: number;
}

export interface CommunityCarouselMedia {
  sha1: string;
  carousel_position: number;
  url: string;
}

/** Community publish metadata fields shared by workflows and tools (differ only by their id field). */
export interface CommunityMetadataBase {
  resource_id: string;
  publishing_status: string;
  community_url: string | null;
  unpublished_at: string | null;
  current_version: WorkflowCommunityVersionMetadata | null;
  content_verified_at: string | null;
  name?: string;
  description?: string;
  category_id?: string;
  tags?: string[];
  support_contact?: string;
  carousel_media?: CommunityCarouselMedia[];
}

export interface WorkflowCommunityMetadata extends CommunityMetadataBase {
  weave_workflow_id: string;
}

/** Community publish metadata for a tool (`weave_app`). Mirrors {@link WorkflowCommunityMetadata}, keyed by `weave_app_id`. */
export interface ToolCommunityMetadata extends CommunityMetadataBase {
  weave_app_id: string;
}

export async function computeFileSha1(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-1', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

const parseS3ErrorCode = async (response: Response): Promise<string | undefined> => {
  try {
    return /<Code>([^<]+)<\/Code>/.exec(await response.text())?.[1];
  } catch {
    return undefined;
  }
};

export interface CommunityUploadError extends Error {
  status?: number;
  s3ErrorCode?: string;
  imageBytes?: number;
}

const uploadCommunityMedia = async (files: File[], endpoint: SafeURL): Promise<string[]> => {
  if (files.length === 0) return [];

  const sha1s = await Promise.all(files.map((file) => computeFileSha1(file)));

  const { data } = await proxied(() =>
    safeAxios.post<SinatraResponseEnvelope<{ presigned_posts: PresignedPost[] }>>(endpoint, {
      images_sha1: sha1s.map((sha1) => ({ sha1 })),
    }),
  );

  const targetsBySha1 = new Map(data.meta.presigned_posts.map((target) => [target.sha1, target]));

  await Promise.all(
    files.map(async (file, i) => {
      const sha1 = sha1s[i];
      const target = targetsBySha1.get(sha1);
      if (!target) throw new Error(`No presigned post returned for sha1 ${sha1}`);

      const formData = new FormData();
      Object.entries(target.fields).forEach(([key, value]) => formData.append(key, value));
      if (file.type) formData.set('Content-Type', file.type);
      formData.append('file', file);

      const uploadUrl = resolveCommunityUploadUrl(target.url);

      await withRetry(
        async () => {
          const response = await fetch(uploadUrl, { method: 'POST', body: formData });
          if (!response.ok) {
            const s3ErrorCode = await parseS3ErrorCode(response);
            const error: CommunityUploadError = new Error(
              `S3 upload failed for sha1 ${sha1}: ${response.status} ${response.statusText}` +
                (s3ErrorCode ? ` (${s3ErrorCode})` : ''),
            );
            throw Object.assign(error, { status: response.status, s3ErrorCode, imageBytes: file.size });
          }
        },
        {
          maxRetries: 3,
          delayMs: 1000,
          shouldRetry: (error) => {
            const status = (error as { status?: number }).status;
            return status === undefined || status >= 500 || status === 408 || status === 429;
          },
        },
      );
    }),
  );

  return sha1s;
};

export async function uploadCommunityImages(files: File[], weaveWorkflowId: string): Promise<string[]> {
  return uploadCommunityMedia(files, API_ROUTES.community.workflowUploads({ weaveWorkflowId }));
}

export interface HandleAvailabilityResponse {
  available: boolean;
  handle: string;
}

export interface CreatedPublisherProfile {
  /** See {@link PublisherProfile.id}. */
  profile_id: string;
  profile_handle: string;
  name: string;
  primary_user_id: number;
  img_url: string;
}

export async function fetchHandleAvailability(handle: string): Promise<HandleAvailabilityResponse> {
  const { data } = await proxied(() =>
    safeAxios.get<SinatraResponseEnvelope<HandleAvailabilityResponse>>(API_ROUTES.community.handleAvailability(), {
      params: { handle },
    }),
  );
  return data.meta;
}

export async function createPublisherProfile(profileHandle: string): Promise<CreatedPublisherProfile> {
  const { data } = await proxied(() =>
    safeAxios.post<SinatraResponseEnvelope<CreatedPublisherProfile>>(API_ROUTES.community.publisherProfiles(), {
      profile_handle: profileHandle,
    }),
  );
  return data.meta;
}

/** Core resolves the Figma user from its stored grant, so no user id is sent. */
export async function fetchPublisherProfiles(): Promise<PublisherProfilesResponse> {
  const { data } = await proxied(() =>
    safeAxios.get<SinatraResponseEnvelope<Partial<PublisherProfilesResponse>>>(
      API_ROUTES.community.publisherProfiles(),
    ),
  );
  return {
    profiles: data.meta.profiles ?? [],
    is_banned_from_community_publishing: data.meta.is_banned_from_community_publishing ?? false,
    is_paid_figma_user: data.meta.is_paid_figma_user ?? false,
    has_accepted_community_creator_agreement: data.meta.has_accepted_community_creator_agreement ?? false,
    community_user_level_creator_agreement_enabled: data.meta.community_user_level_creator_agreement_enabled ?? false,
  };
}

/** One org from Core's eligible-orgs route (`eligibleFigmaOrgsResponseSchema`). IDs stay strings. */
export interface EligibleFigmaOrg {
  plan_id: string;
  name: string;
  /** The org admin's Weave tool publishing setting. */
  publishing_enabled: boolean;
}

/** The linked Figma account's non-guest orgs. Throws {@link NotConnectedToFigmaError} without a grant. */
export async function fetchEligibleFigmaOrgs(): Promise<EligibleFigmaOrg[]> {
  const { data } = await proxied(() =>
    safeAxios.get<SinatraResponseEnvelope<{ orgs: EligibleFigmaOrg[] }>>(API_ROUTES.figmaPublishing.eligibleOrgs()),
  );
  return data.meta.orgs;
}

/** Needs a Figma grant like the rest; the modal only asks once the account is linked. */
export async function fetchCommunityCategories(): Promise<CommunityCategory[]> {
  const { data } = await proxied(() =>
    safeAxios.get<SinatraResponseEnvelope<CommunityCategory[]>>(API_ROUTES.community.categories()),
  );
  return data.meta ?? [];
}

export async function publishWorkflowToCommunity(request: PublishWorkflowRequest): Promise<PublishWorkflowResponse> {
  // The id moves to the path, which is what Core authorizes on.
  const { weave_workflow_id, ...body } = request;
  const { data } = await proxied(() =>
    safeAxios.post<SinatraResponseEnvelope<PublishWorkflowResponse>>(
      API_ROUTES.community.workflowPublish({ weaveWorkflowId: weave_workflow_id }),
      body,
    ),
  );
  return data.meta;
}

export async function fetchWorkflowCommunityMetadata(
  weaveWorkflowId: string,
): Promise<WorkflowCommunityMetadata | null> {
  try {
    const { data } = await proxied(() =>
      safeAxios.get<SinatraResponseEnvelope<WorkflowCommunityMetadata>>(
        API_ROUTES.community.workflowMetadata({ weaveWorkflowId }),
      ),
    );
    return data.meta;
  } catch (error) {
    if (isNotPublished(error)) return null;
    logMetadataFailure('community_metadata_fetch_failed', error);
    throw error;
  }
}

export async function unpublishWorkflowFromCommunity(weaveWorkflowId: string): Promise<void> {
  await proxied(() => safeAxios.post(API_ROUTES.community.workflowUnpublish({ weaveWorkflowId })));
}

// --- Tool (weave_app) publishing ---
// Mirrors the workflow publish path above, keyed by `weave_app_id` (the tool's WorkflowTool.id).

/** Request to publish a Weavy Tool to Community as a `weave_app`. Mirrors {@link PublishWorkflowRequest}. */
export interface PublishToolRequest {
  /** Sent as a path segment; see {@link PublishWorkflowRequest.weave_workflow_id}. */
  weave_app_id: string;
  name: string;
  description?: string;
  carousel_media: CarouselMediaEntry[];
  weavy_version_number: number;
  /** See {@link PublisherProfile.id}. */
  publisher_profile_id: string;
  category_id?: string;
  tags?: string[];
  support_contact?: string;
  agreed_to_tos: boolean;
  /** sha1 of the tool's icon asset; stored as the WeaveAppVersion `icon_path`. Tool-only. */
  icon_sha1?: string;
}

export interface PublishToolResponse {
  resource_id: string;
  weave_app_id: string;
  version_id: string;
  weavy_version_number: number;
}

/**
 * Uploads Community listing images for a tool publish, keyed by `weave_app_id`.
 * Mirrors {@link uploadCommunityImages}; the presigned-post flow is identical.
 */
export async function uploadCommunityToolImages(files: File[], weaveAppId: string): Promise<string[]> {
  return uploadCommunityMedia(files, API_ROUTES.community.toolUploads({ weaveAppId }));
}

/** Publishes a Weavy Tool to Community as a `weave_app`. Mirrors {@link publishWorkflowToCommunity}. */
export async function publishToolToCommunity(request: PublishToolRequest): Promise<PublishToolResponse> {
  const { weave_app_id, ...body } = request;
  const { data } = await proxied(() =>
    safeAxios.post<SinatraResponseEnvelope<PublishToolResponse>>(
      API_ROUTES.community.toolPublish({ weaveAppId: weave_app_id }),
      body,
    ),
  );
  return data.meta;
}

/** Fetches a tool's Community publish metadata, keyed by `weave_app_id`. Mirrors {@link fetchWorkflowCommunityMetadata}. */
export async function fetchToolCommunityMetadata(weaveAppId: string): Promise<ToolCommunityMetadata | null> {
  try {
    const { data } = await proxied(() =>
      safeAxios.get<SinatraResponseEnvelope<ToolCommunityMetadata>>(API_ROUTES.community.toolMetadata({ weaveAppId })),
    );
    return data.meta;
  } catch (error) {
    if (isNotPublished(error)) return null;
    logMetadataFailure('community_tool_metadata_fetch_failed', error);
    throw error;
  }
}

/** Unpublishes a tool from Community, keyed by `weave_app_id`. Mirrors {@link unpublishWorkflowFromCommunity}. */
export async function unpublishToolFromCommunity(weaveAppId: string): Promise<void> {
  await proxied(() => safeAxios.post(API_ROUTES.community.toolUnpublish({ weaveAppId })));
}

/** Owner management status across both destinations, independent of publishing eligibility. */
export interface ToolListingStatus {
  tool_id: string;
  publishing_status: string | null;
  destination:
    | { type: 'org'; plan_id: string; name: string }
    | { type: 'community'; listing_url: string | null; content_verified_at: string | null }
    | null;
}

export async function fetchToolListingStatus(weaveAppId: string): Promise<ToolListingStatus> {
  const { data } = await proxied(() =>
    safeAxios.get<SinatraResponseEnvelope<ToolListingStatus>>(API_ROUTES.figmaPublishing.toolListing({ weaveAppId })),
  );
  return data.meta;
}
